import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Plus, Pencil } from "lucide-react"
import { toast } from "sonner"
import { api, type ApiEnvelope, type Technology } from "@/lib/api"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FieldError } from "@/components/ui/field-error"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { ConfirmDelete } from "@/components/admin/confirm-delete"
import { ImageUpload } from "@/components/admin/image-upload"

export const Route = createFileRoute("/admin/_authed/technologies")({
  component: TechnologiesPage,
})

type FormState = { name: string; order: number; is_active: boolean }
const empty: FormState = { name: "", order: 0, is_active: true }

function TechnologiesPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery<Technology[]>({
    queryKey: ["admin", "technologies"],
    queryFn: async () => (await api.get<ApiEnvelope<Technology[]>>("/admin/technologies")).data.data,
  })

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Technology | null>(null)
  const [form, setForm] = useState<FormState>(empty)
  const [logo, setLogo] = useState<File | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "technologies"] })
    queryClient.invalidateQueries({ queryKey: ["technologies"] })
  }

  const save = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? (v ? "1" : "0") : String(v)))
      if (logo) fd.append("logo", logo)
      if (editing) {
        fd.append("_method", "PUT")
        await api.post(`/admin/technologies/${editing.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      } else {
        await api.post("/admin/technologies", fd, { headers: { "Content-Type": "multipart/form-data" } })
      }
    },
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setFieldErrors({})
      toast.success(editing ? "Technology updated." : "Technology created.")
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  const remove = useMutation({
    mutationFn: async (id: number) => api.delete(`/admin/technologies/${id}`),
    onSuccess: () => {
      invalidate()
      toast.success("Technology deleted.")
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditing(null)
    setForm(empty)
    setLogo(null)
    setFieldErrors({})
    setOpen(true)
  }
  const openEdit = (item: Technology) => {
    setEditing(item)
    setForm({ name: item.name, order: item.order, is_active: item.is_active })
    setLogo(null)
    setFieldErrors({})
    setOpen(true)
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Technologies</h1>
          <p className="mt-1 text-sm text-muted-foreground">Logos shown in the "Technologies We Use" marquee on the home page.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-full">
              <Plus className="h-4 w-4" /> New technology
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit technology" : "New technology"}</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                save.mutate()
              }}
            >
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  aria-invalid={!!fieldErrors.name}
                />
                <FieldError message={fieldErrors.name} />
              </div>
              <ImageUpload label="Logo (optional)" currentUrl={editing?.logo_url} onChange={setLogo} />
              <div className="grid gap-4 sm:grid-cols-2 sm:items-end">
                <div>
                  <Label htmlFor="order">Order</Label>
                  <Input
                    id="order"
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                  />
                </div>
                <div className="flex items-center gap-3 pb-2">
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} id="is_active" />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
        ) : isError ? (
          <p className="text-sm text-destructive">Couldn't load technologies.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(data ?? []).map((t) => (
              <div key={t.id} className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-start justify-between">
                  <div className="flex h-14 w-full items-center justify-center rounded-xl border border-border bg-secondary/50 px-4">
                    {t.logo_url ? (
                      <img src={t.logo_url} alt={t.name} className="h-8 max-w-full object-contain" />
                    ) : (
                      <span className="font-semibold text-foreground">{t.name}</span>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{t.name}</h3>
                    <p className="text-xs text-muted-foreground">Order {t.order}</p>
                  </div>
                  <Badge variant={t.is_active ? "default" : "secondary"}>{t.is_active ? "Active" : "Hidden"}</Badge>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(t)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <ConfirmDelete itemLabel={t.name} onConfirm={() => remove.mutate(t.id)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
